// ============================================================
// useBillEvents.ts — the Activity feed's billing entries (issued bills,
// payments received, issued credit notes) for ONE case or ONE client.
// GET /api/bill-events: a read-time merge, money already masked server-side
// (lib/billMoneyVisibility). Kept live: any bill change on a case in scope
// (or a reconnect / permission change) refetches (useBillingLive).
// Returns { events: null while loading, error, reload } (reload = a quiet
// re-read, e.g. after a bill opened from the feed closes — own-tab writes are
// echo-suppressed, so the live channel won't report them). A load error never blanks
// the rest of the feed — the parent shows it with the feed's other errors.
// ============================================================
import { useCallback, useEffect, useRef, useState } from 'react'
import { listBillEventsApi } from '../api/casevoApi'
import type { BillEvent } from '../types/billing.types'
import { useBillingLive } from './useBillingLive'
import { useCaseChange } from '../utils/caseChangeEvents'

export const useBillEvents = (scope: { caseId: string } | { clientId: string } | null): { events: BillEvent[] | null; error: string | null; reload: () => void } => {
  const [events, setEvents] = useState<BillEvent[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const key = scope ? ('caseId' in scope ? `case:${scope.caseId}` : `client:${scope.clientId}`) : null
  const scopeRef = useRef(scope)
  scopeRef.current = scope
  // Latest load wins (a slow load for the previous client can't land late).
  const seq = useRef(0)
  const load = useCallback(async (): Promise<void> => {
    const s = scopeRef.current
    if (!s) return
    const mine = ++seq.current
    try {
      const data = await listBillEventsApi(s)
      if (mine === seq.current) { setEvents(data); setError(null) }
    } catch (e) {
      if (mine !== seq.current) return
      const ax = e as { response?: { data?: { error?: string } } }
      setEvents([])
      setError(ax?.response?.data?.error ?? 'Couldn’t load billing activity.')
    }
  }, [])
  useEffect(() => {
    setEvents(null); setError(null)
    if (key) void load()
  }, [key, load])
  // Live: a bill changed on a case this scope shows (client scope: any of its
  // cases — events carry their caseId), or a full resync.
  useBillingLive((evs, resync) => {
    const s = scopeRef.current
    if (!s) return
    if (resync || !('caseId' in s) || evs.some((e) => e.caseId === s.caseId)) void load()
  }, { enabled: !!key })
  // (D12) A payment / credit note / bill changed on this case by someone else.
  useCaseChange(scope && 'caseId' in scope ? scope.caseId : null, ['bill'], (_k, info) => { if (info.remote) void load() })
  const reload = useCallback((): void => { void load() }, [load])
  return { events, error, reload }
}
